<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturación <span class="secondary--text">OT</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Análisis de documentos emitidos y estados de cobro</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-invoice-work-orders :filter="filter" :search="search" :options="options" :disabled="loading"/>
      </div>
    </v-row>

    <!-- Metrics Summary Row -->
    <v-row class="mb-8 px-2" v-if="total_sales">
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card pa-4 shadow-xs border-left-primary bg-white">
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="caption grey--text font-weight-black uppercase mb-1">Total Ventas (Periodo)</div>
              <div class="text-h4 font-weight-black secondary--text overflow-hidden text-truncate lh-1">
                {{ total_sales | moneda }}
              </div>
            </div>
            <v-avatar color="primary lighten-5" size="48">
              <v-icon color="primary">fas fa-wallet</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Facturación</div>
          <v-select
            v-model="filter.has_invoiced"
            :items="has_invoiced_options"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined
            clearable
            dense
            hide-details
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <Can I="readAll" a="SalesNote">
            <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Vendedor</div>
            <v-select
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              :loading="sellersLoading"
              outlined
              clearable
              dense
              hide-details
              :disabled="loading"
              class="modern-input"
            >
              <template v-slot:item="{ item }">
                <v-avatar size="24" color="primary lighten-5" class="mr-3">
                  <span class="primary--text font-weight-bold" style="font-size: 10px;">{{ item.first_name ? item.first_name[0] : 'U' }}</span>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="body-2 font-weight-bold">{{ item.first_name }} {{ item.last_name }}</span>
                  <span class="caption grey--text">{{ item.email }}</span>
                </div>
              </template>
            </v-select>
          </Can>
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Tipo de Orden</div>
          <v-select
            v-model="filter.reposicion"
            :items="reposicionOptions"
            item-text="text"
            item-value="value"
            placeholder="Todas"
            outlined
            clearable
            dense
            hide-details
            :disabled="loading"
            class="modern-input"
          />
        </v-col>
      </v-row>
      <v-row dense class="mt-4 px-1 align-center">
        <v-spacer></v-spacer>
        <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="fetch" :loading="loading">
          <v-icon small>fas fa-sync</v-icon>
        </v-btn>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="workOrders"
        :options.sync="options"
        :server-items-length="totalWorkOrders"
        :loading="loading"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Header -->
        <template v-slot:top>
          <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Estado de Facturación de OT ({{ totalWorkOrders }})</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por OT, proyecto, cliente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <template v-slot:item.id="{ item }">
          <show-work-order :work_order_id="item.id">
            <v-chip x-small label dark color="primary" class="font-weight-black">OT #{{ item.id }}</v-chip>
          </show-work-order>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="d-flex flex-column">
            <span class="body-2 font-weight-bold grey--text text--darken-3">{{ item.created_at | moment("DD/MM/YYYY") }}</span>
            <span class="caption grey--text" v-if="item.completed_at">
              Fin: {{ item.completed_at | moment("DD/MM/YYYY") }}
            </span>
          </div>
        </template>

        <template v-slot:item.sales_note__name="{ item }">
          <div class="body-2 font-weight-bold primary--text lh-1-2 py-2">{{ item.name }}</div>
          <div class="caption grey--text font-weight-black uppercase">CN #{{ item.sales_note }}</div>
        </template>

        <template v-slot:item.sales_note__customer__name="{ item }">
          <div class="caption font-weight-bold grey--text text--darken-2">{{ item.customer_name }}</div>
          <div class="caption grey--text d-flex align-center mt-1">
            <v-icon x-small class="mr-1" color="grey lighten-1">fas fa-user-tie</v-icon>
            {{ item.seller_name || 'Sin asignar' }}
          </div>
        </template>

        <template v-slot:item.total_sale="{ item }">
          <div class="body-2 font-weight-black grey--text text--darken-2">{{ item.real_price.sales_total | moneda }}</div>
        </template>

        <template v-slot:item.total_invoiced_payed.invoiced="{ item }">
          <div class="d-flex flex-column align-end">
             <div class="body-1 font-weight-black secondary--text tracking-tighter">{{ item.total_invoiced_payed.invoiced | moneda }}</div>
             <v-chip v-if="item.total_invoiced_payed.invoiced >= item.real_price.sales_total" x-small label dark color="success" class="mt-1 font-weight-black px-1" style="height: 14px;">
               FACTURADO
             </v-chip>
             <v-chip v-else-if="item.total_invoiced_payed.invoiced > 0" x-small label dark color="warning" class="mt-1 font-weight-black px-1" style="height: 14px;">
               PARCIAL
             </v-chip>
             <v-chip v-else x-small label outlined color="grey lighten-1" class="mt-1 font-weight-black px-1 grey--text" style="height: 14px;">
               PENDIENTE
             </v-chip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- detail Dialog -->
    <v-dialog v-model='display' max-width="1200" scrollable transition="dialog-bottom-transition">
      <v-card class="modern-card shadow-lg">
        <v-toolbar flat class="border-bottom">
          <v-avatar color="primary lighten-5" size="32" class="mr-3">
            <v-icon color="primary" x-small>fas fa-info-circle</v-icon>
          </v-avatar>
          <v-toolbar-title class="body-1 font-weight-black primary--text uppercase">
            Detalle de Órden #{{ workOrderOnDisplay.id }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="display = false" class="grey lighten-4">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <work-order-card :work_order='workOrderOnDisplay' @close="display=false"/> 
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { fetchPostCostWorkOrders } from '@/api/reports'
import { fetchSellers } from '@/api/auth'
import CustomerComplete2 from '@/components/customers/CustomerComplete2.vue'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import ExportInvoiceWorkOrders from '@/components/reports/ExportInvoiceWorkOrders'
import ShowWorkOrder from '@/components/production/ShowWorkOrder.vue'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'InvoicedWorkOrders',
  components: { 
    CustomerComplete2, 
    WorkOrderCard, 
    ExportInvoiceWorkOrders,
    ShowWorkOrder
  },
  data() {
    return {
      loading: false,
      has_invoiced_options: [
        { text: 'Facturado', value: 'true' },
        { text: 'No Facturado', value: 'false' },
      ],
      reposicionOptions: [
        { text: 'Reposición', value: 'true' },
        { text: 'Normal', value: 'false' },
      ],
      filter: {
        created_after: moment().startOf('month').format('YYYY-MM-DD'),
        created_before: '',
        seller: '',
        has_invoiced: 'true',
        reposicion: '',
      },
      workOrderOnDisplay: {},
      options: {},
      totalWorkOrders: 0,
      display: false,
      search: '',
      workOrders: [],
      total_sales: 0,
      sellers: [],
      sellersLoading: false,
      timer: null,
      headers: [
        { text: 'OT', align: 'start', value: 'id', class: 'table-header', width: '110px' },
        { text: 'Cronología', align: 'start', value: 'created_at', class: 'table-header', width: '140px' },
        { text: 'Proyecto', align: 'start', value: 'sales_note__name', class: 'table-header' },
        { text: 'Cliente', align: 'start', value: 'sales_note__customer__name', class: 'table-header' },
        { text: 'Venta Neto', align: 'end', value: 'total_sale', class: 'table-header', sortable: false, width: '140px' },
        { text: 'Monto Facturado', align: 'end', value: 'total_invoiced_payed.invoiced', class: 'table-header', sortable: false, width: '160px' },
      ]
    }
  },
  async mounted() {
    this.sellersLoading = true
    try {
      this.sellers = await fetchSellers()
    } catch (err) {
      console.error(err)
    } finally {
      this.sellersLoading = false
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      let created_before = ''
      let created_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().endOf('day').format('YYYY-MM-DD HH:mm')
      
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      if (!this.$can('readAll', 'SalesNote')) {
        const user = this.$store.getters.getUser
        this.filter.seller = user.id
      }

      let ordering = sortDesc && sortDesc[0] ? sortBy[0] : (sortBy && sortBy[0] ? '-' + sortBy[0] : '-created_at')

      const query = `ordering=${ordering}&page=${page || 1}&page_size=${itemsPerPage || 10}&search=${this.search}&created_after=${created_after}&created_before=${created_before}&sales_note__customer__seller=${this.filter.seller || ''}&has_invoices=${this.filter.has_invoiced || ''}&sales_note__reposicion=${this.filter.reposicion || ''}`
      
      try {
        const data = await fetchPostCostWorkOrders(query)
        this.totalWorkOrders = data.count
        this.workOrders = data.results
        this.total_sales = data.total_sales || 0
      } catch (err) {
        notifications.showError('Error Cargando Ordenes de Trabajo: ' + err)
      } finally {
        this.loading = false
      }
    },
    showWorkOrder(item) {
      this.workOrderOnDisplay = item
      this.display = true
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.fetch()
      }, 700);
    }
  },
  watch: {
    options: {
      handler: 'fetch',
      deep: true
    },
    filter: {
      deep: true,
      handler() {
        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.fetch()
        }
      }
    },
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }

/* Modern Card Styles */
.modern-card { 
  border-radius: 12px !important; 
  border: 1px solid rgba(0,0,0,0.05) !important; 
  background-color: white !important; 
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.shadow-lg { box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 56px !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}
.modern-table >>> td {
  height: 64px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}
.modern-search-field { max-width: 400px; }
.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Utilities */
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.0px; }
.border-white-sm { border: 2px solid white !important; }
</style>